<script setup>
import { Head, useForm, usePage } from '@inertiajs/vue3';
import Layout from '@/Layouts/Layout.vue';
import DatePicker from '@/Components/DatePicker.vue';
import {
    CreditCardIcon,
    KeyIcon,
    CheckCircleIcon,
} from '@heroicons/vue/24/outline/index.js';
import {
    formatValue,
    parkingWallClockNow,
    toWallClockString,
} from '../helpers.js';
import { computed, onMounted, ref } from 'vue';

defineOptions({
    layout: Layout,
});

defineProps({
    products: Array,
});

const page = usePage();
const isReservationClosed = page.props.config.reservationClosed;

const isClient = typeof window !== 'undefined';

if (isClient) {
    import('@googlemaps/js-api-loader').then(({ Loader }) => {
        const loader = new Loader({
            apiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
            version: 'weekly',
        });

        loader.load().then(async () => {
            let AdvancedMarkerElement;
            const { Map } = await google.maps.importLibrary('maps');
            ({ AdvancedMarkerElement } =
                await google.maps.importLibrary('marker'));

            const map = new Map(document.getElementById('map'), {
                mapId: '97626b319e43743d',
                mapTypeId: 'roadmap',
                center: {
                    lat: 47.384971618652344,
                    lng: 11.268280029296875,
                },
                zoom: 16,
                streetViewControl: false,
            });

            new AdvancedMarkerElement({
                position: { lat: 47.384971618652344, lng: 11.268280029296875 },
                map: map,
                title: 'Parkplatz Scharnitz',
            });
        });
    });
}

const form = useForm({
    reservationDate: toWallClockString(parkingWallClockNow()),
});

const changeReservationDate = (date) => {
    form.reservationDate = toWallClockString(date.value);
};

const currencyOptions = {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
};
const productPriceFormatted = (amount) => {
    return formatValue(amount / 100, currencyOptions);
};

const faqArray = [
    {
        question: 'Kann ich auch vor Ort einen Parkschein lösen?',
        answer: 'Ja, du kannst vor Ort einfach online buchen. Barzahlung ist nicht möglich.',
    },
    {
        question: 'Ist mir ein Parkplatz zugesichert?',
        answer: 'Ja! Sobald du eine Bestätigung von uns erhalten hast, ist dir ein Parkplatz garantiert.',
    },
    {
        question:
            'Was tun, wenn etwas mit meiner Reservierung schief gegangen ist?',
        answer: 'Keine Sorge! Einfach per Mail unter office@parkplatz-scharnitz.at kontaktieren.',
    },
    {
        question:
            'Ist das Parkticket auch beim Informationszentrum in Scharnitz gültig?',
        answer: 'Nein. Der Parkplatz gehört nicht zur Gemeinde Scharnitz und deshalb ist auch das Ticket dort nicht gültig. Bitte parken Sie an der richtigen Adresse.',
    },
    {
        question: 'Gilt die Straßenverkehrsordnung am Parkplatz?',
        answer: 'Die Vorschriften der Straßenverkehrsordnung (StVO) sind bindend und gelten für alle Verkehrsteilnehmer.',
    },
    {
        question: 'Der Betreiber übernimmt keine Haftung!',
        answer: 'Die Nutzung des Parkplatzes erfolgt auf eigene Gefahr. Der Betreiber übernimmt keine Haftung für Schäden oder Verluste, die durch Dritte, Unfälle, Diebstahl oder sonstige Umstände entstehen.',
    },
    {
        question: 'Woher bekomme ich eine Rechnung?',
        answer: 'Schreib uns einfach eine Mail mit deinen Daten und wir senden dir eine Rechnung.',
    },
];

const tipps = [
    {
        title: 'Gleierschklamm',
        description:
            'Ein wunderschöner Spaziergang der nicht zu anspruchsvoll ist!',
        badges: ['Wandern', 'Leicht', '~2h'],
        image: '/assets/klamm-foto.jpeg',
    },
    {
        title: 'Hallerangeralm',
        description:
            'Eine traumhafte bewirtschaftete Almhütte mit Schlafmöglichkeiten.',
        badges: ['Wandern & Radfahren', 'Mittel', '~6h'],
        image: '/assets/halleranger-alm.jpeg',
    },
    {
        title: 'Karwendelhaus',
        description:
            'Die Hütte bietet den optimalen Ausgangspunkt für anspruchsvolle Bergtouren.',
        badges: ['Wandern & Radfahren', 'Leicht', '~4,5h'],
        image: '/assets/karwendel-haus.jpg',
    },
];

const comeFromGermany = ref(true);

const reviews = [
    {
        text: 'Tolle Parkmöglichkeit! Hat sehr einfach funktioniert.',
        image: '/assets/portrait-1.jpg',
    },
    {
        text: 'Super einfach und unkompliziert. Unsere Wanderung danach war traumhaft!',
        image: '/assets/portrait-2.jpg',
    },
    {
        text: 'Wir konnten schon im Voraus buchen und mussten uns keine Gedanken mehr machen.',
        image: '/assets/portrait-3.jpg',
    },
];

const minDate = computed(() => {
    let minDate = parkingWallClockNow();

    const minutes = minDate.getMinutes();
    const minutesToSubtract = minutes % 15;
    minDate.setMinutes(minutes - minutesToSubtract);
    minDate.setSeconds(0);

    return minDate;
});

const jsonLD = {
    '@context': 'https://schema.org',
    '@type': 'ParkingFacility',
    name: 'Parkplatz Scharnitz – Eisack 112',
    description:
        'Parkplatz direkt am Einstieg zur Gleierschklamm. Einfach online reservieren – ab €3 für 3 Stunden, €5 halbtags, €7 ganztags oder €14 für 3 Tage.',
    url: 'https://parkplatz-scharnitz.at',
    telephone: '+43', // optional ergänzen
    address: {
        '@type': 'PostalAddress',
        streetAddress: 'Eisack 112',
        addressLocality: 'Scharnitz',
        postalCode: '6108',
        addressCountry: 'AT',
    },
    geo: {
        '@type': 'GeoCoordinates',
        latitude: 47.38497,
        longitude: 11.26828,
    },
    openingHours: ['Mo-Su 00:00-24:00'],
    priceRange: '€3 - €14',
    amenityFeature: [
        {
            '@type': 'LocationFeatureSpecification',
            name: 'Online reservierbar',
            value: true,
        },
        {
            '@type': 'LocationFeatureSpecification',
            name: 'Nähe Gleierschklamm, nähe Hinterautal, nähe Karwendel',
            value: true,
        },
    ],
    image: [
        'https://parkplatz-scharnitz.at/assets/parkplatz-scharnitz-hero.jpg',
    ],
    offers: [
        {
            '@type': 'Offer',
            name: '3 Stunden',
            price: '3',
            priceCurrency: 'EUR',
        },
        {
            '@type': 'Offer',
            name: 'Halber Tag',
            price: '5',
            priceCurrency: 'EUR',
        },
        {
            '@type': 'Offer',
            name: 'Ganzer Tag',
            price: '7',
            priceCurrency: 'EUR',
        },
        {
            '@type': 'Offer',
            name: 'Bis zu 3 Tage',
            price: '14',
            priceCurrency: 'EUR',
        },
    ],
};

onMounted(() => {
    console.log('mounting');

    const jsonLdTag = document.createElement('script');
    jsonLdTag.type = 'application/ld+json';
    jsonLdTag.innerHTML = JSON.stringify(jsonLD);
    document.head.appendChild(jsonLdTag);
});
</script>

<template>
    <div>
        <Head>
            <title>{{ $t('index.meta.title') }}</title>
            <meta name="description" :content="$t('index.meta.description')" />
            <meta name="robots" content="index, follow" />
            <meta property="og:title" :content="$t('index.meta.title')" />
            <meta
                property="og:description"
                :content="$t('index.meta.description')"
            />
            <meta name="twitter:title" :content="$t('index.meta.title')" />
            <meta
                name="twitter:description"
                :content="$t('index.meta.description')"
            />
        </Head>

        <div
            class="absolute top-16 left-0 w-full h-[32rem] md:h-[44rem] lg:h-[50rem] -z-10"
        >
            <span class="bg-none absolute top-0 left-0 w-full h-full">
                <img
                    class="w-full h-full object-cover opacity-80"
                    alt="Hiking map"
                    src="/assets/bg-hero.png"
                />
            </span>
        </div>
        <div id="#booking" class="container pt-16 mx-auto">
            <section
                class="w-full flex flex-col justify-center items-center h-[32rem] md:h-[44rem] lg:h-[50rem]"
            >
                <h1 class="text-3xl font-black uppercase text-center">
                    {{ $t('index.hero.title') }}
                </h1>
                <form
                    v-if="!isReservationClosed"
                    @submit.prevent="form.get('/reservation')"
                >
                    <div class="mt-16 space-y-4 flex flex-col justify-center">
                        <h3 class="text-lg uppercase font-bold text-center">
                            {{ $t('index.hero.arrival_time') }}
                        </h3>
                        <DatePicker
                            v-model="form.reservationDate"
                            :min="minDate"
                            @change="changeReservationDate"
                        />

                        <button
                            type="submit"
                            :disabled="form.processing"
                            class="btn btn-primary"
                        >
                            {{ $t('index.hero.continue_booking') }}
                        </button>
                    </div>
                </form>
                <p v-else class="text-lg mt-4">
                    {{ $t('index.hero.closed') }}
                </p>
            </section>

            <section class="mt-12 md:mt-32 md:px-10">
                <h3 class="text-center uppercase font-bold">
                    {{ $t('index.how_it_works.title') }}
                </h3>
                <div
                    class="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 text-center mx-8"
                >
                    <div class="flex flex-col items-center">
                        <CreditCardIcon class="size-1/3" />
                        <h4 class="mt-4 font-bold">
                            {{ $t('index.how_it_works.booking.title') }}
                        </h4>
                        <p class="mt-2">
                            {{ $t('index.how_it_works.booking.description') }}
                        </p>
                    </div>
                    <div class="flex flex-col items-center">
                        <KeyIcon class="size-1/3" />
                        <h4 class="mt-4 font-bold">
                            {{ $t('index.how_it_works.arrival.title') }}
                        </h4>
                        <p class="mt-2">
                            {{ $t('index.how_it_works.arrival.description') }}
                        </p>
                    </div>
                    <div class="flex flex-col items-center">
                        <CheckCircleIcon class="size-1/3" />
                        <h4 class="mt-4 font-bold">
                            {{ $t('index.how_it_works.enjoyment.title') }}
                        </h4>
                        <p class="mt-2">
                            {{ $t('index.how_it_works.enjoyment.description') }}
                        </p>
                    </div>
                </div>
            </section>

            <section class="text-center mt-12 md:mt-32 mx-8">
                <h3 class="uppercase font-bold">
                    {{ $t('index.location.title') }}
                </h3>
                <div class="my-3 flex justify-center gap-4 align-center">
                    <button
                        class="btn btn-sm"
                        :class="
                            comeFromGermany
                                ? 'btn-primary'
                                : 'btn-primary-outline'
                        "
                        @click="comeFromGermany = true"
                    >
                        {{ $t('index.location.from_germany') }}
                    </button>
                    <button
                        class="btn btn-sm"
                        :class="
                            !comeFromGermany
                                ? 'btn-primary'
                                : 'btn-primary-outline'
                        "
                        @click="comeFromGermany = false"
                    >
                        {{ $t('index.location.from_innsbruck') }}
                    </button>
                </div>
                <p class="text-center md:px-20 xl:px-40">
                    {{
                        comeFromGermany
                            ? $t('index.location.directions.from_germany')
                            : $t('index.location.directions.from_innsbruck')
                    }}, {{ $t('index.location.directions.common') }}
                </p>
                <div class="mt-8 w-full flex justify-center">
                    <div id="map" class="w-full h-96 rounded-xl"></div>
                </div>
            </section>

            <section class="text-center mt-12 md:mt-32">
                <h3 class="uppercase font-bold">
                    {{ $t('index.pricing.title') }}
                </h3>
                <div class="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4 mx-8">
                    <div
                        v-for="(product, index) in products"
                        :key="index"
                        class="card border border-solid border-neutral text-primary h-60"
                    >
                        <div
                            class="card-body items-center justify-center text-center"
                        >
                            <h2 class="card-title flex-col">
                                <span class="text-4xl md:text-5xl">{{
                                    productPriceFormatted(product.amount)
                                }}</span>
                                <span class="md:text-xl font-normal">{{
                                    $t('products.' + product.meta_name)
                                }}</span>
                            </h2>
                        </div>
                    </div>
                </div>
                <div class="mt-4">
                    <button type="button" class="btn btn-primary">
                        <a href="#booking">
                            {{ $t('index.pricing.reserve_now') }}
                        </a>
                    </button>
                </div>
            </section>

            <section class="text-center mt-12 md:mt-32">
                <h3 class="uppercase font-bold">
                    {{ $t('index.reviews.title') }}
                </h3>
                <div class="mt-4 mx-8 grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div
                        v-for="(review, index) in reviews"
                        :key="index"
                        class="card border border-primary/20 shadow-xl text-primary col-span-1"
                    >
                        <div class="card-body">
                            <h2 class="card-title justify-center">
                                <div class="rating">
                                    <input
                                        type="radio"
                                        disabled
                                        class="mask mask-star cursor-default bg-[#daa520]"
                                    />
                                    <input
                                        type="radio"
                                        disabled
                                        class="mask mask-star cursor-default bg-[#daa520]"
                                    />
                                    <input
                                        type="radio"
                                        disabled
                                        class="mask mask-star cursor-default bg-[#daa520]"
                                    />
                                    <input
                                        type="radio"
                                        disabled
                                        class="mask mask-star cursor-default bg-[#daa520]"
                                    />
                                    <input
                                        type="radio"
                                        disabled
                                        class="mask mask-star cursor-default text-[#daa520]"
                                        checked="checked"
                                    />
                                </div>
                            </h2>
                            <div class="mt-4 flex align-center gap-x-4">
                                <div class="avatar">
                                    <div class="w-16 h-16 rounded-full">
                                        <img
                                            :src="review.image"
                                            alt="Ein Bild von einem Bewerter"
                                        />
                                    </div>
                                </div>
                                <p class="text-left">
                                    {{ $t('index.reviews.items.' + index) }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section class="text-center mt-12 md:mt-32">
                <h3 class="uppercase font-bold">
                    {{ $t('index.tips.title') }}
                </h3>
                <div
                    class="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mx-8"
                >
                    <div
                        v-for="(tip, index) in tipps"
                        :key="index"
                        class="card bg-base-100 shadow-xl"
                    >
                        <figure class="h-60">
                            <img
                                :src="tip.image"
                                :alt="'Ein Foto der Empfehlung ' + tip.title"
                            />
                        </figure>
                        <div class="card-body">
                            <h2 class="card-title justify-center">
                                {{ $t('index.tips.items.' + index + '.title') }}
                            </h2>
                            <p>
                                {{
                                    $t(
                                        'index.tips.items.' +
                                            index +
                                            '.description',
                                    )
                                }}
                            </p>
                            <div class="card-actions justify-center">
                                <div
                                    v-for="(badge, i) in tip.badges"
                                    :key="i"
                                    class="badge badge-outline"
                                >
                                    {{ badge }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <section class="text-center mt-12 md:mt-32">
                <h3 class="uppercase font-bold">
                    {{ $t('index.faq.title') }}
                </h3>
                <div class="mt-8 space-y-4">
                    <div
                        v-for="(faq, index) in faqArray"
                        :key="index"
                        class="join join-vertical w-full"
                    >
                        <div
                            class="collapse collapse-arrow join-item border border-base-300"
                        >
                            <input
                                class="w-full"
                                type="radio"
                                name="faq-section"
                            />
                            <div
                                class="collapse-title text-xl font-medium"
                                v-html="
                                    $t(
                                        'index.faq.questions.' +
                                            index +
                                            '.question',
                                    )
                                "
                            ></div>
                            <div class="collapse-content">
                                <p
                                    v-html="
                                        $t(
                                            'index.faq.questions.' +
                                                index +
                                                '.answer',
                                        )
                                    "
                                ></p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <section class="text-center my-32">
                <h3 class="text-2xl font-bold">
                    {{ $t('index.booking.title') }}
                </h3>

                <div class="mt-4">
                    <a href="#booking">
                        <button type="button" class="btn btn-primary">
                            {{ $t('index.booking.button') }}
                        </button>
                    </a>
                </div>
            </section>
        </div>
    </div>
</template>
